import React, { useEffect, useState, Fragment } from 'react'
import { css } from 'emotion'
import * as R from 'ramda'
import { connect } from 'react-redux'
import sortBy from 'lodash/sortBy'
import config from '../../config'
import ProfileDropdown from './ProfileDropDown'
import FlyerBoardDropDown from './FlyerBoardDropDown'
import useProfile from '../hooks/useProfile'
import PortalBffClientHttp from '../api/http/PortalBffClientHttp'
import AccountsBffClientHttp from '../api/http/AccountsBffClientHttp'
const { colors, components } = require('@peachjar/components')

const { Brandbar: PJBrandbar } = components
const { REACT_APP_ACCOUNTS_APP_URL } = config

const portalHttpClient = new PortalBffClientHttp()
const accountHttpClient = new AccountsBffClientHttp()
const BRANDBAR_MOBILE_HEIGHT = '51px'

const PeachjarBrandBar = ({ viewOptions }) => {
  const profile = useProfile()
  const {    
    scopes,
    loading
  } = profile  
  const userId = (profile.userId || '').toString()

  const [flags, setFlags] = useState({
    portal_nav: 1,
    portal_nav_banner: 0,
    upload_flyer: 0,
    approval_center: 0,
    approval_center_banner: 0,
    org_uploader: 0,
    managed_services: 0,
    comm_free: 0,
  })
  const [linkedAccounts, setLinkedAccounts] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [showProfile, setShowProfile] = useState(false)
  useEffect(() => {
    const getFlags = async () => {
      const featureFlags = await portalHttpClient.getFeatureFlags()      
      setFlags(featureFlags)
    }

    const getLinkedAccounts = async () => {
      const { data } = await accountHttpClient.getLinkedAccounts()
      if (data.success) {
        const linkedAccountList = []
        for (const account of data.linkedAccounts) {
          if (account.scopes.includes('staff') || account.scopes.includes('volunteer')) {
            const entities = await getRolesForUser(account.userId)
            const entity = await getEntity(entities)
            if (entity) {
              linkedAccountList.push({
                ...account,
                hierarchy: {
                  id: entity.id.toString(),
                  name: `${entity.name}`,
                  type: entity.type
                }
              })
              continue
            }
          }
          linkedAccountList.push(account)
        }
        setLinkedAccounts(linkedAccountList)
      }
    }

    const getUserSubscriptions = async () => {
      const { data: { success, subscriptions } } = await portalHttpClient.getSubscriptions()
      if (success && subscriptions) {
        const subscriptionsList = []
        const activeSubscriptions = subscriptions.filter(({ status }) => status === 'active')
        for(let subscription of activeSubscriptions){
          const schoolId = Number(subscription.audienceId)
          const {data: { success, name }} = await portalHttpClient.getSchoolBySchoolId(schoolId)
          if(success){
            subscriptionsList.push({
              ...subscription,
              name
            })
          }
        }
        const districtIds = [...new Set(subscriptionsList.map(subscription => Number(subscription.hierarchyId)))]        
        const districts = [];
        for (let districtId of districtIds) {          
          const {data: { success, name }} = await portalHttpClient.getDistrictNameById(districtId)          
          if(success){
            const subscriptions = sortBy(
              subscriptionsList.filter(subscriptions => Number(subscriptions.hierarchyId) === districtId),
              subscription => subscription.name
            )
            
            districts.push({
              districtName: name, 
              districtId,
              subscriptions
            })
          }
        }        
        const subscriptionsGrouped = sortBy(districts, district => district.districtName)
        setSubscriptions(subscriptionsGrouped)
      }    
    }
    
    if(!loading) {      
      if(userId && !scopes.includes('staff') && !scopes.includes('org_member')) {
        getFlags()
        getLinkedAccounts()
        getUserSubscriptions()
        setShowProfile(true)
      }
    }
  }, [loading])

  useEffect(() => {
    if (!loading && viewOptions.length > 0 && !userId && subscriptions.length === 0) {
      setSubscriptions(viewOptions)
    }
  }, [viewOptions, loading])

  const getRolesForUser = async (userIdParam) => {
    const { data: { entities } } = await portalHttpClient.getRolesForUser(
      userIdParam.toString()
    )

    if (entities.length > 0) {
      return entities
    }
    return []
  }

  const getEntity = async (entities) => {
    if (entities.length > 0) {
      const districtEntities = entities.filter(({ forEntity: { entityType } }) => entityType === 'district')
      let userEntities = districtEntities
      if (districtEntities.length === 0) {
        userEntities = entities.filter(({ forEntity: { entityType } }) => entityType === 'school')
      }

      const { forEntity } = userEntities[0]
      const entityId = Number(forEntity.entityId)
      let name = ''
      if (forEntity.entityType === 'district') {
        name = await getDistrictNameById(entityId)
      } else {
        name = await getSchoolNameById(entityId)
      }
      if (name) {
        const moreEntities = forEntity.entityType === 'school' ? entities.length > 1 ? `+more` : '' : ''
        return {
          id: entityId,
          name: `${name} ${moreEntities}`,
          type: forEntity.entityType
        }
      }
    }
    return null
  }

  const getDistrictNameById = async (entityId) => {
    const { data: { success, name }} =  await portalHttpClient.getDistrictNameById(entityId)
    if (success) {
      return name
    }

    return null
  }

  const getSchoolNameById = async (entityId) => {
    const { data: { success, name }} =  await portalHttpClient.getSchoolBySchoolId(entityId)
    if (success) {
      return name
    }

    return null
  }

  const getUserType = (scopes) => {
    if (scopes.includes('parent')) {
      return 'Parent'
    }

    if (scopes.includes('volunteer')) {
      return 'Volunteer'
    }

    if (scopes.includes('staff')) {
      return 'Staff'
    }

    if (scopes.includes('org_member')) {
      return 'Organization'
    }
    
    return ''
  }

  const userType = getUserType(scopes)
  const firstName = profile.firstName || userType
  const lastName = profile.lastName || ''
  const emailAddress = profile.email || 'No email provided.'
  const hierarchyType = profile.hierarchy ? profile.hierarchy.type : ''
  let accountName = ''
  
  const account = linkedAccounts.find(account => account.userId.toString() === userId.toString())
  if (account) {
    accountName = userType === 'Parent' ? firstName !== userType ? 'Parent' : '' : account.hierarchy.name
  }

  return (
      <PJBrandbar
          className={classNames.brandbar}
          dark={true}
      >
        {!profile.loading && (
          <div className={classNames.brandbarContent}>
            {subscriptions.length > 0 &&
              <FlyerBoardDropDown
                options={subscriptions}
                isAuthenticated={showProfile}
              />
            }
           {showProfile ? (
               <ProfileDropdown
                 userId={userId}
                 entityName={accountName}
                 hierarchyType={hierarchyType}
                 linkedAccounts={linkedAccounts}
                 firstName={firstName}
                 lastName={lastName}
                 emailAddress={emailAddress}
                 flags={flags}
                 hideProfileLinks={false}
                 getUserType={getUserType}
               />
             )
            : (
              <Fragment>
                <span className={classNames.divider}></span>
                <div className={classNames.linksContainer}>
                  <div>
                    <a
                        href={`${REACT_APP_ACCOUNTS_APP_URL}/signup`}
                        className={classNames.link}
                    >
                        Sign Up
                    </a>
                  </div>
                  <div>
                    <a
                        href={`${REACT_APP_ACCOUNTS_APP_URL}/login`}
                        className={`${classNames.link} ${classNames.logInLink}`}
                    >
                        Log In
                    </a>
                  </div>
              </div>
              </Fragment>

            )
           }
          </div> 
        )}                
      </PJBrandbar>
  )
}

const classNames = {
    brandbar: css`
      z-index: 50;
      @media (max-width: 480px) {
        min-height: ${BRANDBAR_MOBILE_HEIGHT};
        height: ${BRANDBAR_MOBILE_HEIGHT};
        max-height: ${BRANDBAR_MOBILE_HEIGHT};
      }
    `,
    brandbarContent: css`
      flex: 1 1 auto;
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-end;
      align-items: center;
      margin-left: 1rem;
      & button {
        background-color: #47506b !important;
        & > span:first-child {
          border: none !important;
          background-color:#47506b !important;
        }
        & span {
          color: #fff !important;
        }
      }
      & button:focus {
        outline: none;
      }
  `,
  linksContainer: css`
    display: flex;
    margin-right: 1rem;
  `,
  link: css`
    display: flex;
    align-items: center;
    margin-left: auto;
    color: ${colors.white};    
    padding: 6px 16px;
    border-radius: 3px;
    &:hover {
      color: ${colors.white};
      text-decoration: none;
      background-color: rgba(45, 52, 75, 0.5);
    }
 `,
 logInLink: css`
   border: 1px solid ${colors.white};
   margin-left: 5px;
 `,
  divider: css`
    border-left: 1px solid #2d344b;
    height: 100%;
    margin-right: 4px;
  `,
}

const mapStateToProps = (state, props) => {
  const district = state.districts.district
  const districtSchools = R.map(id => state.schools[id], district.schools)
  const nonStaffSchools = R.filter(R.propEq('staff', false), districtSchools)
  const subscriptions = [
    {
      name: district.name,
      audienceId: district.district_id,
      audienceType: 'district'
    },
    ...nonStaffSchools.map(school => ({
      name: school.name,
      audienceId: school.school_id,
      audienceType: 'school'
    }))
  ]

  const viewOptions = district.name ?
    [
      {
        districtName: district.name,
        districtId: district.district_id,
        subscriptions
      }
    ] : []

  return {
    districtId: district.district_id,
    viewOptions
  }
}

export default connect(mapStateToProps, null)(PeachjarBrandBar)



// WEBPACK FOOTER //
// ./src/shared/components/Brandbar.js